
import { CaregiverProfile, Badge } from '../types/index';
import { env } from '../../../config/env.js';

// Helper function to extract text from HTML list items
const extractListItems = (html: string, sectionHeader: string): string[] => {
    if (!html) return [];
    const regex = new RegExp(`${sectionHeader}.*?<ul>(.*?)<\/ul>`, 's');
    const match = html.match(regex);
    if (match && match[1]) {
        const items = match[1].match(/<li>(.*?)<\/li>/g);
        if (items) {
            return items.map(item => item.replace(/<\/?li>/g, '').trim());
        }
    }
    return [];
};

// Helper to check if JWT token is expired
const isTokenExpired = (token: string): boolean => {
    try {
        if (!token) return true;
        const base64Url = token.split('.')[1];
        if (!base64Url) return true;

        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const { exp } = JSON.parse(jsonPayload);
        // exp is in seconds, Date.now() is in ms
        return Date.now() >= exp * 1000;
    } catch (e) {
        console.error("Error checking token expiry", e);
        return true;
    }
};

// Helper function to get file download URL
const getFileDownloadUrl = async (fileUrl: string): Promise<string | null> => {
    if (!fileUrl) return null;
    
    try {
        const response = await fetch(`${env.UPLOAD_ENDPOINT_URL}/agents-images?url=${encodeURIComponent(fileUrl)}`, {
            method: 'GET'
        });
        
        if (response.ok) {
            const data = await response.json();
            return data.downloadUrl || null;
        }
    } catch (error) {
        console.error("Error fetching file download URL:", error);
    }
    return null;
};

export const calculateAge = (dateOfBirth: string, today = new Date()): number | null => {
    // Date-only values must be parsed as calendar dates, rather than UTC
    // timestamps, so the displayed age does not change with the viewer's timezone.
    const dateParts = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const birthDate = dateParts
        ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
        : new Date(dateOfBirth);

    if (Number.isNaN(birthDate.getTime())) return null;

    let age = today.getFullYear() - birthDate.getFullYear();
    const birthdayHasPassed =
        today.getMonth() > birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    if (!birthdayHasPassed) age -= 1;
    return age >= 0 ? age : null;
};

export const transformData = (data: any): CaregiverProfile => {
    const { details, skills } = data;

    // DOB is authoritative; the persisted age field can become stale over time.
    const ageFromDob = details.dob ? calculateAge(details.dob) : null;
    const age = ageFromDob ?? details.age;

    // Process skills into categories and derived lists
    const badges: { [category: string]: Badge[] } = {};
    const specialties: string[] = [];
    const languages: string[] = [];
    const services: string[] = [];

    skills.forEach((skill: any) => {
        let category = "Other Skills";
        const path = skill.active_image_path || "";

        if (path.includes('/ADL/')) {
            category = "Activities of Daily Living";
        } else if (path.includes('/HOM/')) {
            category = "Home Care";
            services.push(skill.badge);
        } else if (path.includes('/LAN/')) {
            category = "Languages";
            languages.push(skill.badge);
        } else if (path.includes('/experiences_active/')) {
            category = "Specialized Care";
            specialties.push(skill.badge);
        }

        if (!badges[category]) {
            badges[category] = [];
        }
        badges[category].push({
            name: skill.badge,
            iconUrl: skill.active_image_path
        });
    });

    // Extract Certifications from Biography HTML
    const certifications = extractListItems(details.biography || "", 'Certification');

    return {
        name: details.name,
        imageUrl: details.image_url_s3,
        tagline: details.profession_type || "Professional Caregiver",
        location: `${details.state}`,
        isVerified: true, // Assuming true for this dataset
        about: details.biography || "",
        experience: "Experienced Professional", // Default as explicit years aren't in a structured field
        specialties: specialties.length > 0 ? specialties : ["General Care"],
        languages: languages.length > 0 ? languages : ["English"],
        certifications: certifications,
        services: services,
        badges: badges,
        testimonials: [], // No testimonials in source data
        nationality: details.nationality,
        maritalStatus: details.marital_status || "Not Specified",
        age: age || 0,
        gender: details.sex ? details.sex.charAt(0).toUpperCase() + details.sex.slice(1) : "Not Specified",
        workExperience: data.details.work_experience || [],
        certs: data.certs || []
    };
};

export const caregiverService = {
  getProfile: async (agentId: string): Promise<CaregiverProfile> => {
    if (!agentId) {
        throw new Error("Missing Agent ID");
    }

    try {
        const response = await fetch(`${env.API_ENDPOINT_URL}/careadmin/carepro-profile?agent_id=${agentId}`);
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !data.details) {
            throw new Error("Failed to load profile data");
        }

        const token = data.details.profile_token;
        if (isTokenExpired(token)) {
            throw new Error("TOKEN_EXPIRED");
        }

        const profile = transformData(data);

        // Fetch secure image URL
        try {
            const s3Url = data.details.image_url_s3;
            if (s3Url) {
                const imgResponse = await fetch(`${env.IMAGE_ENDPOINT_URL}/carepro-profile-images?url=${encodeURIComponent(s3Url)}`, {
                    method: 'GET',
                    headers: {
                        "x-api-key": env.IMAGE_ENDPOINT_KEY,
                    }
                });
                
                if (imgResponse.ok) {
                    const imgData = await imgResponse.json();
                    if (imgData.success && imgData.downloadUrl) {
                        profile.imageUrl = imgData.downloadUrl;
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching secure image URL:", error);
            // Non-critical error, continue with original URL or default
        }

        // Fetch download URLs for certification files asynchronously (non-blocking)
        if (profile.certs && profile.certs.length > 0) {
            profile.certs.forEach(async (cert) => {
                if (cert.file.imageUrlS3) {
                    const downloadUrl = await getFileDownloadUrl(cert.file.imageUrlS3);
                    if (downloadUrl) {
                        cert.file.downloadUrl = downloadUrl;
                    }
                }
            });
        }

        return profile;

    } catch (error: any) {
        throw error;
    }
  },
};
